import { FC, Ref, useEffect, useImperativeHandle, useMemo, useState } from 'react';

import { PackagingGroup } from '@ravago/shared/page-data/models/elements';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Button } from '@ravago/shared/radiance/components/button/components/Button/Button';
import { IconButton } from '@ravago/shared/radiance/components/button/components/IconButton/IconButton';
import { Icon } from '@ravago/shared/radiance/components/icon/components/Icon/Icon';
import { Dropdown, DropdownOption } from '@ravago/shared/radiance/components/input-field/components/Dropdown/Dropdown';
import { InputField } from '@ravago/shared/radiance/components/input-field/components/InputField/InputField';
import { Sublevel } from '@ravago/shared/radiance/components/sublevel/components/Sublevel/Sublevel';
import { Tooltip } from '@ravago/shared/radiance/components/tooltip/components/Tooltip/Tooltip';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

import { Controller, useForm, useWatch } from 'react-hook-form';

import { RequestForQuotationStep } from '../../../../models/request-for-quotation-step';

export interface FormValue {
   packaging: DropdownOption;
   packagingDescription: string | undefined;
   quantityBreaks: { value: string }[];
}

export interface LocalFormValue {
   packaging: DropdownOption | undefined;
   packagingDescription: string | undefined;
   quantityBreaks: { value: string }[];
}

interface Props {
   stepRef: Ref<RequestForQuotationStep>;
   packagingGroups: PackagingGroup[];
   uom: string;
   initialFormValue: LocalFormValue | undefined;
   defaultFormValue: FormValue | undefined;
   onSubmit: (value: FormValue) => void;
}

// Returns the values that are marked as duplicate
const findDuplicates = (arr: string[]): string[] => {
   const countMap = new Map<string, number>();
   const duplicates = new Set<string>();

   arr.forEach((value) => {
      countMap.set(value, (countMap.get(value) || 0) + 1);
      if (countMap.get(value)! > 1) {
         duplicates.add(value);
      }
   });

   return Array.from(duplicates);
};

const getDefaultPackaging = (packagingGroups: PackagingGroup[], packagingOptions: DropdownOption[]) => {
   const defaultPackagingGroup = packagingGroups.find((packagingGroup) => packagingGroup.defaultPackaging);
   if (defaultPackagingGroup) {
      return packagingOptions.find((option) => option.id === defaultPackagingGroup.id.toString());
   }
   return undefined;
};

export const RfqFlyoutStepQuotationDetails: FC<Props> = ({ stepRef, packagingGroups, uom, initialFormValue, defaultFormValue, onSubmit }) => {
   const translate = useTranslations();

   const packagingOptions = useMemo(
      () =>
         packagingGroups.map((group) => ({
            id: group.id.toString(),
            value: translate(`packaging-groups.${group.description.toLowerCase().replace(' ', '-')}`)
         })),
      [packagingGroups, translate]
   );

   const {
      control,
      trigger,
      formState: { errors, isSubmitted },
      setValue,
      setFocus,
      handleSubmit
   } = useForm<LocalFormValue, unknown, FormValue>({
      defaultValues: defaultFormValue ??
         initialFormValue ?? {
            packaging: getDefaultPackaging(packagingGroups, packagingOptions),
            packagingDescription: '',
            quantityBreaks: [{ value: '' }]
         }
   });

   // HACK: There is a problem with useFieldArray that when an item is removed it is still in the validation array
   // might be related to https://github.com/react-hook-form/react-hook-form/issues/12791
   const quantityBreaks = useWatch({
      control,
      name: 'quantityBreaks'
   });

   const [lastAddedQuantityBreakIndex, setLastAddedQuantityBreakIndex] = useState(-1);
   const [isQuantityBreaksSubmitted, setIsQuantityBreaksSubmitted] = useState(false);

   useEffect(() => {
      if (lastAddedQuantityBreakIndex === -1) return;
      setFocus(`quantityBreaks.${lastAddedQuantityBreakIndex}.value`);
      setLastAddedQuantityBreakIndex(-1);
   }, [lastAddedQuantityBreakIndex]);

   useImperativeHandle(stepRef, () => ({
      submit: async () => {
         await handleSubmit((value) => onSubmit(value))();
      }
   }));

   const handleAddQuantity = async () => {
      setIsQuantityBreaksSubmitted(true);
      const result = await trigger('quantityBreaks');
      if (!result) return;
      setIsQuantityBreaksSubmitted(false);
      if (quantityBreaks.length === 5) return;
      const newFields = [...quantityBreaks, { value: '' }];
      setValue('quantityBreaks', newFields);
      setLastAddedQuantityBreakIndex(quantityBreaks.length);
   };

   const handleRemoveQuantity = async (index: number) => {
      const newFields = [...quantityBreaks];
      newFields.splice(index, 1);
      setValue('quantityBreaks', newFields, { shouldValidate: isSubmitted });
      await trigger('quantityBreaks');
   };

   const packaging = useWatch({ control, name: 'packaging' });
   const showPackagingDescription = packaging?.id === '-2'; // -2 is equal to Other

   return (
      <>
         <Box direction="column" gap="md">
            <Typography component="span" variant="body-highlight">
               {translate('page-consumer.request-for-quotation.packaging')}
            </Typography>
            <Controller
               control={control}
               name="packaging"
               rules={{ required: translate('form-validation.required', { field: translate('form-field.packaging') }) }}
               render={({ field: { ref, ...field } }) => (
                  <Dropdown
                     inputRef={ref}
                     {...field}
                     options={packagingOptions}
                     id="rfq-packaging-dropdown"
                     label={translate('page-consumer.request-for-quotation.your-packaging-preferences')}
                     emptyOption={false}
                     error={!!errors.packaging}
                     errorMessage={errors.packaging?.message?.toString()}
                  />
               )}
            />
            {showPackagingDescription && (
               <Sublevel>
                  <Controller
                     control={control}
                     name="packagingDescription"
                     shouldUnregister
                     render={({ field: { ref, ...field } }) => (
                        <InputField
                           inputRef={ref}
                           {...field}
                           label={translate('page-consumer.request-for-quotation.describe-packaging-details')}
                           supportText={translate('page-consumer.request-for-quotation.other-packaging-description')}
                           id="rfq-other-packaging-description"
                        />
                     )}
                  />
               </Sublevel>
            )}
         </Box>

         <Box gap="md" direction="column">
            <Box direction="row" gap="2xs" items="center">
               <Typography component="span" variant="body-highlight">
                  {translate('page-consumer.request-for-quotation.quantity')}
               </Typography>
               <Tooltip align="start" pointers text={translate('page-consumer.request-for-quotation.tooltip.quantity')}>
                  <Box>
                     <Icon icon="info-circle" />
                  </Box>
               </Tooltip>
            </Box>

            <Box direction="column" gap="md">
               {quantityBreaks.map((item, index, array) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <Box direction="row" gap="xs" key={index}>
                     <Controller
                        control={control}
                        name={`quantityBreaks.${index}.value`}
                        rules={{
                           required: translate('form-validation.required', { field: translate('form-field.quantity') }),
                           min: {
                              value: 1,
                              message: translate('form-validation.min', { field: translate('form-field.quantity'), value: 1 })
                           },
                           max: {
                              value: 999999999,
                              message: translate('form-validation.max', {
                                 field: translate('form-field.quantity'),
                                 value: 999999999
                              })
                           },
                           validate: {
                              unique: (currentValue, formValue) => {
                                 const allValues = formValue.quantityBreaks.map((v) => v.value);
                                 const duplicates = findDuplicates(allValues);
                                 return duplicates.includes(currentValue) ? translate('form-validation.unique') : undefined;
                              }
                           }
                        }}
                        render={({ field: { ref, onChange, ...field }, fieldState: { error } }) => (
                           <InputField
                              inputRef={ref}
                              {...field}
                              type="number"
                              id={`quantity-break-${index}`}
                              label={translate('page-consumer.request-for-quotation.enter-quantity')}
                              suffix={uom}
                              error={!!error}
                              errorMessage={error?.message}
                              onChange={(e) => {
                                 onChange(e);
                                 if (isSubmitted || isQuantityBreaksSubmitted) trigger('quantityBreaks');
                              }}
                              onEnter={handleAddQuantity}
                           />
                        )}
                     />
                     <IconButton
                        variant="danger"
                        icon="trash"
                        title={translate('page-consumer.request-for-quotation.remove-quantity')}
                        ariaLabel={`Remove quantity ${index + 1}`}
                        disabled={array.length === 1}
                        onClick={() => handleRemoveQuantity(index)}
                     />
                  </Box>
               ))}

               {quantityBreaks.length < 5 ? (
                  <Box direction="row-reverse">
                     <Button variant="tertiary" onClick={handleAddQuantity} id="rfq-flyout-add-quantity-btn">
                        {translate('page-consumer.request-for-quotation.add-quantity')}
                     </Button>
                  </Box>
               ) : (
                  <Typography variant="caption">{translate('page-consumer.request-for-quotation.max-number-quantities-reached')}</Typography>
               )}
            </Box>
         </Box>
      </>
   );
};
